import { GlossaryV2Dto } from "@/shared/dtos/glossary-v2-dto";
import { RecycleGlossaryService } from "@/shared/services/recycleGlossaryService";
import { useEffect, useState } from "react";

interface UseRecycleGlossaryResult {
	glossaries: GlossaryV2Dto[];
	pageIndex: number;
	pageSize: number;
	error: Error | null;
	fetchDeletedGlossaries: () => Promise<void>;
	handlePageIndexChanged: (offset: number) => void;
	handlePageSizeChanged: (limit: number) => void;
	restoreGlossary: (id: string) => Promise<boolean>;
	permanentDeleteGlossary: (id: string) => Promise<boolean>;
}

/**
 * Hook personalizado para manejar la carga y paginación de glosarios eliminados
 */
export const useRecycleGlossary = (
	initialPageSize = 10,
	initialPageIndex = 0,
): UseRecycleGlossaryResult => {
	const [glossaries, setGlossaries] = useState<GlossaryV2Dto[]>([]);
	const [pageIndex, setPageIndex] = useState<number>(initialPageIndex);
	const [pageSize, setPageSize] = useState<number>(initialPageSize);
	const [error, setError] = useState<Error | null>(null);

	const fetchDeletedGlossaries = async (): Promise<void> => {
		setError(null);

		try {
			const result: GlossaryV2Dto[] =
				await RecycleGlossaryService.getDeletedGlossaries();
			const mappedGlossaries = result.map((x) => new GlossaryV2Dto(x));

			setGlossaries(mappedGlossaries);
		} catch (err) {
			const error = err as Error;
			setError(error);
		}
	};

	const restoreGlossary = async (id: string): Promise<boolean> => {
		try {
			await RecycleGlossaryService.restoreGlossary(id);
			// Refrescar la lista después de restaurar
			await fetchDeletedGlossaries();
			return true;
		} catch (err) {
			const error = err as Error;
			setError(error);
			return false;
		}
	};

	const permanentDeleteGlossary = async (id: string): Promise<boolean> => {
		try {
			await RecycleGlossaryService.permanentDeleteGlossary(id);
			await fetchDeletedGlossaries();
			return true;
		} catch (err) {
			const error = err as Error;
			setError(error);
			return false;
		}
	};

	const handlePageIndexChanged = (offset: number): void => {
		setPageIndex(offset);
	};

	const handlePageSizeChanged = (limit: number): void => {
		setPageSize(limit);
		setPageIndex(0);
	};

	useEffect(() => {
		fetchDeletedGlossaries();
	}, [pageSize, pageIndex]);

	return {
		glossaries,
		pageIndex,
		pageSize,
		error,
		fetchDeletedGlossaries,
		handlePageIndexChanged,
		handlePageSizeChanged,
		restoreGlossary,
		permanentDeleteGlossary,
	};
};
